import { LoadingPaymentForm } from '@/components/checkout/Payments';
import { CheckoutContext } from '@/contexts/CheckoutContext';
import useDevice from '@/hooks/useDevice';
import useSettings from '@/hooks/useSettings';
import LoadingIcon from '@/icons/LoadingIcon';
import { PaymentMethod } from '@/types';
import { CheckoutDeviceType } from '@/types/builder';
import { canPaymentMethodBePaidInInstallments } from '@/utils/payment';
import { Tab } from '@headlessui/react';
import { ComponentType, useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { tv } from 'tailwind-variants';

type PaymentTabItem = {
  id: PaymentMethod;
  label: string;
  Icon: ComponentType<any>;
  Component: ComponentType<any>;
};

const tab = tv({
  base: `
  border
  border-[#919EAB33]
  flex
  cursor-pointer
  font-medium
  gap-3
  h-[100px]
  hover:shadow-lg
  items-center
  justify-center
  outline-none
  p-[10px]
  rounded-md
  shadow-md
  shadow-sm
  flex-col
  text-center
  text-sm
  `,
  variants: {
    status: {
      active: 'bg-[#0F7864]',
      inactive: 'text-gray-700',
    },
    width: {
      fullMobile: 'w-[90px]',
      minMobile: 'flex-1',
      minDesktop: 'flex-1',
      fullDesktop: 'w-[150px]',
    },
  },
});

export default function PaymentMethods() {
  const form = useFormContext();
  const { setValue } = form;
  const { offer, setPaymentMethod, paymentTabs, paymentMethod, isFetching } = useContext(CheckoutContext);
  const [tabIndex, setTabIndex] = useState(0);
  const settings = useSettings();
  const { device } = useDevice();

  const tabs = useMemo<PaymentTabItem[]>(() => {
    if (isFetching) {
      return Array.from({ length: 6 }).map((_, i) => ({
        id: String(i + 1) as PaymentMethod,
        label: 'loading',
        Icon: LoadingIcon,
        Component: LoadingPaymentForm,
      }));
    }

    const paymentsOrder = offer?.product?.paymentsOrder || [];

    const isIOS =
      /ipad|iphone|ipod/i.test(navigator.userAgent) && !/android/i.test(navigator.userAgent);
    const isAndroid = /android/i.test(navigator.userAgent);

    const rawTabs = (paymentTabs ?? []) as PaymentTabItem[];

    const sortedTabs = rawTabs
      .filter(({ id }) => offer?.product.paymentMethods?.some(({ type }) => type === id))
      .sort((a, b) => paymentsOrder.indexOf(a.id) - paymentsOrder.indexOf(b.id));

    if (isAndroid) {
      const applePaymentIndex = sortedTabs.findIndex((t) => t.id === 'applepay');
      if (applePaymentIndex !== -1) sortedTabs.splice(applePaymentIndex, 1);
    } else if (isIOS) {
      const googlePaymentIndex = sortedTabs.findIndex((t) => t.id === 'googlepay');
      if (googlePaymentIndex !== -1) sortedTabs.splice(googlePaymentIndex, 1);
    }

    const openFinanceIndex = sortedTabs.findIndex((t) => t.id === 'openfinance_nubank');
    if (openFinanceIndex > -1) {
      const [openFinanceTab] = sortedTabs.splice(openFinanceIndex, 1);
      sortedTabs.unshift(openFinanceTab);
    }

    return sortedTabs;
  }, [paymentTabs, offer, isFetching]);

  const handleTabStyle = useMemo(() => {
    return (index: number, currentTabIndex: number, id: string) => {
      const isSelected = index === currentTabIndex;
      const { paymentOptions } = settings;
      const { button } = paymentOptions || {};
      const selected = button?.selected;
      const unselected = button?.unselected;

      const styles = {
        background: unselected?.background.color || '',
        icon: unselected?.icon.color || '',
        text: unselected?.text.color || '',
      };

      if (isSelected) {
        styles.background = selected?.background.color || '';
        styles.icon = selected?.icon.color || '';
        styles.text = selected?.text.color || '';
      }

      if (id === 'openfinance_nubank') {
        styles.background = isSelected ? '#2f0549' : '#820AD1';
        styles.icon = selected?.icon.color || '';
        styles.text = selected?.text.color || '';
      }

      return styles;
    };
  }, [settings]);

  const onChangeTab = (index: number) => {
    const selectedTab = tabs?.[index];
    if (!selectedTab) return;

    setValue('paymentMethod', selectedTab.id);

    if (!canPaymentMethodBePaidInInstallments(selectedTab.id)) {
      setValue('installments', 1);
    }

    setPaymentMethod({
      type: selectedTab.id,
      name: selectedTab.label,
    });

    setTabIndex(index);
  };

  useEffect(() => {
    if (!paymentMethod) {
      const tabsOrder = tabs?.map(({ id }) => id) || [];

      const allPaymentMethods = (offer?.product.paymentMethods || [])
        .filter(({ type }) => type !== 'threeDs')
        .sort((a, b) => tabsOrder.indexOf(a.type) - tabsOrder.indexOf(b.type));

      const defaultPaymentMethodResult = offer?.product?.paymentMethods
        .filter(({ type }) => type !== 'threeDs')
        .find(({ type }) => type === offer?.product?.defaultPaymentMethod);

      const creditCardPaymentMethod = allPaymentMethods.find(({ type }) => type === 'credit_card');

      let defaultPaymentMethod = defaultPaymentMethodResult || creditCardPaymentMethod;

      if (!defaultPaymentMethod) {
        defaultPaymentMethod = allPaymentMethods[0];
      }

      if (!defaultPaymentMethod) return;

      setValue('paymentMethod', defaultPaymentMethod.type as PaymentMethod);
      setPaymentMethod(defaultPaymentMethod);

      const index = tabs.findIndex((item) => item.id === (defaultPaymentMethod?.type as PaymentMethod));
      setTabIndex(index >= 0 ? index : 0);
    }
  }, [offer, tabs, paymentMethod, setPaymentMethod, setValue]);

  useEffect(() => {
    if (isFetching) return;
    if (!paymentMethod) return;
    if (!paymentTabs || paymentTabs.length === 0) return;

    const ids = paymentTabs.map((t: any) => t.id as PaymentMethod);
    const exists = ids.includes(paymentMethod.type as PaymentMethod);
    if (exists) return;

    const firstFromPaymentTabs = (paymentTabs[0] as any)?.id as PaymentMethod | undefined;
    const fallback = tabs?.[0]?.id as PaymentMethod | undefined;

    const nextId = firstFromPaymentTabs || fallback;
    if (!nextId) return;

    const nextTab = tabs.find((t) => t.id === nextId) || tabs[0];
    if (!nextTab) return;

    setValue('paymentMethod', nextTab.id);

    if (!canPaymentMethodBePaidInInstallments(nextTab.id)) {
      setValue('installments', 1);
    }

    setPaymentMethod({
      type: nextTab.id,
      name: nextTab.label,
    });

    const nextIndex = tabs.findIndex((t) => t.id === nextTab.id);
    setTabIndex(nextIndex >= 0 ? nextIndex : 0);
  }, [isFetching, paymentMethod, paymentTabs, tabs, setPaymentMethod, setValue]);

  const getTabWidth = (): 'fullMobile' | 'minMobile' | 'minDesktop' | 'fullDesktop' => {
    const desktopRows = offer?.checkout?.config?.desktop?.rows || [];
    const lastDesktopRows = desktopRows[desktopRows.length - 1]?.layout;

    if (device === CheckoutDeviceType.MOBILE) {
      if (tabs.length < 4) return 'minMobile';
      return 'fullMobile';
    }

    if (['[4,8]', '[4,4,4]'].includes(JSON.stringify(lastDesktopRows))) {
      if (tabs.length < 4) return 'minMobile';
      return 'fullMobile';
    }

    if (tabs.length < 6) return 'minDesktop';
    return 'fullDesktop';
  };

  return (
    <Tab.Group onChange={onChangeTab} selectedIndex={tabIndex}>
      <Tab.List className="flex flex-row flex-wrap justify-center rounded-xl gap-2" color="green">
        {tabs?.map(({ Icon, label, id }, index) => (
          <Tab
            style={{ backgroundColor: handleTabStyle(index, tabIndex, id).background }}
            key={id}
            className={({ selected }) =>
              tab({
                status: selected ? 'active' : 'inactive',
                width: getTabWidth(),
              })
            }
          >
            {({ selected }) => (
              <>
                <Icon
                  className={`box-border h-6 w-6 sm:h-8 sm:w-8 ${label === 'loading' && 'animate-pulse'}`}
                  fill={handleTabStyle(index, tabIndex, id).icon}
                />
                <span
                  style={{ color: handleTabStyle(index, tabIndex, id).text }}
                  className={`xs:text-[10px] sm:text-sm font-semibold leading-tight ${selected ? 'text-white' : 'text-[#919EAB]'
                    }`}
                >
                  {label === 'loading' ? (
                    <div className="bg-gray-200 h-[15px] rounded-full w-20 animate-pulse dark:bg-gray-300"></div>
                  ) : (
                    label
                  )}
                </span>
              </>
            )}
          </Tab>
        ))}
      </Tab.List>
      <Tab.Panels className="w-full">
        {tabs?.map(({ Component, id }) => (
          <Tab.Panel className="w-full mt-3" key={id}>
            <Component />
          </Tab.Panel>
        ))}
      </Tab.Panels>
    </Tab.Group>
  );
}
